import { create } from 'zustand';

const useMessageStore = create((set, get) => ({
  // Хранение диалогов по ID документа
  conversationsByDocument: {},
  
  // Состояние загрузки
  loadingStates: {},
  
  // Флаги для прокрутки вниз
  shouldScrollToBottom: {},
  
  // Получение диалогов для конкретного документа
  getConversations: (documentId) => {
    const { conversationsByDocument } = get();
    return conversationsByDocument[documentId] || [];
  },
  
  // Установка состояния загрузки
  setLoading: (documentId, isLoading) => {
    set((state) => ({
      loadingStates: {
        ...state.loadingStates,
        [documentId]: isLoading
      }
    }));
  },
  
  // Проверка состояния загрузки
  isLoading: (documentId) => {
    const { loadingStates } = get();
    return loadingStates[documentId] || false;
  },
  
  // Добавление новой беседы (вопрос с опциональным ответом)
  addConversation: (documentId, question, answer = null, tempId = null) => {
    const now = new Date();
    set((state) => {
      const currentConversations = state.conversationsByDocument[documentId] || [];
      return {
        conversationsByDocument: {
          ...state.conversationsByDocument,
          [documentId]: [
            ...currentConversations,
            { 
              question, 
              answer,
              tempId, // Добавляем временный ID для отслеживания
              questionTimestamp: now.toISOString(),
              answerTimestamp: answer ? now.toISOString() : null,
              timestamp: now.toISOString()
            },
          ]
        }
      };
    });
  },
  
  // Обновление существующей беседы
  updateConversation: (documentId, index, updates) => {
    set((state) => {
      const currentConversations = state.conversationsByDocument[documentId] || [];
      if (index < 0 || index >= currentConversations.length) return state;
      
      const updatedConversations = [...currentConversations];
      updatedConversations[index] = {
        ...updatedConversations[index],
        ...updates
      };
      
      return {
        conversationsByDocument: {
          ...state.conversationsByDocument,
          [documentId]: updatedConversations
        }
      };
    });
  },
  
  // Обновление последней беседы (добавление ответа)
  updateLastConversation: (documentId, updates) => {
    const now = new Date();
    set((state) => {
      const currentConversations = state.conversationsByDocument[documentId] || [];
      if (currentConversations.length === 0) return state;
      
      const lastIndex = currentConversations.length - 1;
      const updatedConversations = [...currentConversations];
      updatedConversations[lastIndex] = {
        ...updatedConversations[lastIndex],
        ...updates,
        answerTimestamp: updates.answer ? now.toISOString() : null
      };
      
      return {
        conversationsByDocument: {
          ...state.conversationsByDocument,
          [documentId]: updatedConversations
        }
      };
    });
  },
  
  // Загрузка истории диалогов с поддержкой пагинации
  fetchConversations: async (documentId, lastQuestionId = null, append = false, shouldScrollToBottom = false) => {
    try {
      const token = localStorage.getItem('token');
      let url = `${process.env.REACT_APP_API_URL}/documents/${documentId}/questions`;
      
      // Добавляем параметры пагинации, если есть lastQuestionId
      if (lastQuestionId) {
        url += `?last_question_id=${lastQuestionId}`;
      }
      
      const response = await fetch(url, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      const data = await response.json();
      
      // Преобразуем полученные вопросы в формат диалогов
      const newConversations = data.questions.map(q => {
        // Убедимся, что даты из бэкенда правильно интерпретируются как UTC
        let questionTimestamp = q.created_at;
        let answerTimestamp = q.completed_at;
        
        // Если даты не содержат информацию о часовом поясе, добавляем 'Z' (UTC)
        if (questionTimestamp && typeof questionTimestamp === 'string' && 
            !questionTimestamp.endsWith('Z') && !questionTimestamp.includes('+')) {
          questionTimestamp += 'Z';
        }
        
        if (answerTimestamp && typeof answerTimestamp === 'string' && 
            !answerTimestamp.endsWith('Z') && !answerTimestamp.includes('+')) {
          answerTimestamp += 'Z';
        }
        
        return {
          id: q.question_id,
          question: q.question,
          answer: q.answer || null,
          questionTimestamp: questionTimestamp || new Date().toISOString(),
          answerTimestamp: answerTimestamp || null,
          timestamp: new Date().toISOString()
        };
      });
      
      set((state) => {
        // Если append=true, добавляем новые сообщения к существующим (для пагинации)
        if (append && state.conversationsByDocument[documentId]) {
          return {
            conversationsByDocument: {
              ...state.conversationsByDocument,
              [documentId]: [
                ...newConversations,
                ...state.conversationsByDocument[documentId]
              ]
            },
            shouldScrollToBottom: {
              ...state.shouldScrollToBottom,
              [documentId]: shouldScrollToBottom
            }
          };
        } else {
          // Получаем текущие беседы
          const currentConversations = state.conversationsByDocument[documentId] || [];
          
          // Если это обычное обновление (не первая загрузка), проверяем изменения
          if (currentConversations.length > 0) {
            // Создаем карту существующих сообщений по ID для быстрого поиска
            const existingConversationsMap = {};
            currentConversations.forEach(conv => {
              if (conv.id) {
                existingConversationsMap[conv.id] = conv;
              }
            });
            
            // Проверяем, есть ли новые сообщения или обновления
            let hasChanges = false;
            const updatedConversations = [...currentConversations];
            
            // Проходим по новым сообщениям
            for (const newConv of newConversations) {
              // Проверяем, есть ли сообщение с таким же вопросом среди временных
              const tempIndex = updatedConversations.findIndex(c => 
                c.tempId && c.question === newConv.question
              );
              
              // Если нашли временное сообщение с таким же вопросом, заменяем его
              if (tempIndex !== -1) {
                updatedConversations[tempIndex] = {
                  ...newConv,
                  // Сохраняем временный ID для последующей идентификации
                  tempId: updatedConversations[tempIndex].tempId
                };
                hasChanges = true;
                continue;
              }
              
              // Если сообщение уже существует, проверяем изменения
              if (newConv.id && existingConversationsMap[newConv.id]) {
                const existingConv = existingConversationsMap[newConv.id];
                // Если ответ изменился, обновляем
                if (newConv.answer !== existingConv.answer) {
                  // Находим индекс существующего сообщения
                  const index = updatedConversations.findIndex(c => c.id === newConv.id);
                  if (index !== -1) {
                    // Обновляем только если есть изменения
                    updatedConversations[index] = {
                      ...existingConv,
                      answer: newConv.answer,
                      answerTimestamp: newConv.answerTimestamp
                    };
                    hasChanges = true;
                  }
                }
              } else {
                // Проверяем, нет ли уже сообщения с таким же вопросом
                // (чтобы избежать дублирования)
                const existingIndex = updatedConversations.findIndex(c => 
                  c.question === newConv.question && !c.tempId
                );
                
                if (existingIndex === -1) {
                  // Это новое сообщение, добавляем его
                  updatedConversations.push(newConv);
                  hasChanges = true;
                }
              }
            }
            
            // Если нет изменений, возвращаем текущее состояние
            if (!hasChanges) {
              return state;
            }
            
            // Сортируем сообщения по времени создания (новые внизу)
            updatedConversations.sort((a, b) => {
              const timeA = new Date(a.questionTimestamp || a.timestamp);
              const timeB = new Date(b.questionTimestamp || b.timestamp);
              return timeA - timeB;
            });
            
            return {
              conversationsByDocument: {
                ...state.conversationsByDocument,
                [documentId]: updatedConversations
              },
              shouldScrollToBottom: {
                ...state.shouldScrollToBottom,
                [documentId]: shouldScrollToBottom && hasChanges
              }
            };
          } else {
            // Первая загрузка, просто устанавливаем новые сообщения
            return {
              conversationsByDocument: {
                ...state.conversationsByDocument,
                [documentId]: newConversations
              },
              shouldScrollToBottom: {
                ...state.shouldScrollToBottom,
                [documentId]: shouldScrollToBottom
              }
            };
          }
        }
      });
      
      // Важно: возвращаем количество полученных сообщений для проверки, есть ли еще сообщения
      // console.log(`Fetched ${newConversations.length} messages, lastQuestionId: ${lastQuestionId}`);
      return newConversations.length;
    } catch (error) {
      console.error('Error fetching conversations:', error);
      return 0;
    }
  },
  
  // Отправка нового сообщения
  sendMessage: async (documentId, text) => {
    if (!text.trim() || !documentId) return;
    
    // Генерируем временный ID для нового сообщения
    const tempId = `temp-${Date.now()}`;
    
    // Добавляем новую беседу только с вопросом и временным ID
    get().addConversation(documentId, text, null, tempId);
    get().setLoading(documentId, true);
    
    // Устанавливаем флаг для прокрутки вниз
    set((state) => ({
      shouldScrollToBottom: {
        ...state.shouldScrollToBottom,
        [documentId]: true
      }
    }));
    
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${process.env.REACT_APP_API_URL}/documents/${documentId}/questions`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ question: text }),
      });
      
      const data = await response.json();
      
      // Обновляем временное сообщение, заменяя его на сообщение с реальным ID
      set((state) => {
        const currentConversations = state.conversationsByDocument[documentId] || [];
        const updatedConversations = currentConversations.map(conv => {
          // Если это наше временное сообщение, обновляем его ID и добавляем ответ
          if (conv.tempId === tempId) {
            return {
              ...conv,
              id: data.question_id,
              tempId: undefined,
              answer: data.answer || null,
              answerTimestamp: data.answer ? new Date().toISOString() : null
            };
          }
          return conv;
        });
        
        return {
          conversationsByDocument: {
            ...state.conversationsByDocument,
            [documentId]: updatedConversations
          }
        };
      });
    } catch (error) {
      console.error('Error sending message:', error);
    } finally {
      get().setLoading(documentId, false);
    }
  }
}));

export default useMessageStore; 
